import { Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, tap } from 'rxjs';
import {
  NavigationItem,
  RoutePath,
} from '../interfaces/navigationList.interface';
import { FiltersLocation } from '../interfaces/region.interface';
import {
  HomeButtonConfig,
  Profile,
} from '../interfaces/PageContainerConfiguration.interface';
import { PermissionsService } from '@core/services/permissions.service';

@Injectable({
  providedIn: 'root',
})
export class PageContainerService {
  private navigationList: NavigationItem[] = [];
  private routePath: RoutePath[] = [];
  activeSectionRoute = new BehaviorSubject<NavigationItem | null>(null);
  menuExpanded = new BehaviorSubject<boolean>(false);
  resetRouting$ = new BehaviorSubject<any>(null);
  changeTab$ = new BehaviorSubject<NavigationItem | null>(null);
  selectLastChild$ = new BehaviorSubject<NavigationItem | null>(null);
  selectedLocations$ = new BehaviorSubject<FiltersLocation[]>([]);

  expandMenuDrawer$ = new BehaviorSubject<boolean>(false);
  expandFilterDrawer$ = new BehaviorSubject<boolean>(false);

  homePageConfig = signal<HomeButtonConfig | null>(null);
  profileConfig = signal<Profile | null>(null);
  standAlonePageTitle = signal<string | null>(null);

  routerEvent$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    tap(event => {
      this.getActiveRoute(event);
    }),
  );

  constructor(
    readonly router: Router,
    readonly permissionsService: PermissionsService,
  ) {}

  initializeRouterEvent() {
    this.routerEvent$.subscribe();
  }

  setNavigationList(navigationList: NavigationItem[]) {
    this.navigationList = navigationList;
  }

  getNavigationList(): NavigationItem[] {
    return this.navigationList;
  }

  setRoutePath(routePath: RoutePath[]) {
    this.routePath = routePath;
  }

  getRoutePath(): RoutePath[] {
    return this.routePath;
  }

  private getActiveRoute(event: NavigationEnd) {
    this.resetRouting$.next(null);
    const currentRoutePath = this.routePath.find(
      x => x.url === event.urlAfterRedirects,
    )?.routePath;
    if (currentRoutePath) {
      const index = this.navigationList.findIndex(
        x => x.id === currentRoutePath[0],
      );
      if (index !== -1) {
        this.activeSectionRoute.next(this.navigationList[index]);
        this.standAlonePageTitle.set(this.navigationList[index].name);
      } else {
        throw new Error('Route id not found');
      }
    } else if (event.urlAfterRedirects === this.homePageConfig()?.routeUrl) {
      this.standAlonePageTitle.set(this.homePageConfig()?.title ?? null);
    } else if (event.urlAfterRedirects === this.profileConfig()?.profileRoute) {
      this.standAlonePageTitle.set('Profile');
    } else {
      this.standAlonePageTitle.set(null);
    }
  }

  handleInitialNavigation(url: string) {
    this.getActiveRoute({ urlAfterRedirects: url } as NavigationEnd);
  }

  resetRouting() {
    this.activeSectionRoute.next(null);
    this.resetRouting$.next(null);
  }

  filterNavigationList(items: NavigationItem[]): NavigationItem[] {
    return items
      .map(item => {
        // Recursively process children
        const filteredChildren = this.filterNavigationList(item.children || []);

        // Check if the current item has permissions
        const parentHasPermission =
          item.permissions &&
          this.permissionsService.hasAnyPermission(item.permissions);

        // Retain item if:
        // - It has its own permissions
        // - It has children with valid permissions (filteredChildren is not empty)
        if (parentHasPermission || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren, // Include only filtered children
          };
        }

        // Exclude items without permissions or valid children
        return null;
      })
      .filter(item => item !== null); // Remove null items
  }
}
